<template>
  <tr :class="{ 'orange lighten-5': data.stats.injured }">
    <td>
      <div
        class="d-flex align-center"
        style="white-space: nowrap; width: auto;"
      >
        <span>{{ $dayjs(data.date).format('ll - ddd') }}</span>
        <v-spacer />
        <span v-if="data.stats.injured">
          <v-chip
            class="ml-1"
            label
            small
            color="orange darken-2"
            dark
          >
            {{ $t("player_status.injured.capitalized") }}
          </v-chip>
        </span>
      </div>
    </td>
    <td>
      <table-bar-value
        :value="data.stats.ACWR"
        :max="maxValues.ACWR"
        :color="getAcwrColorFromValue(data.stats.ACWR)"
      >
        {{ data.stats.ACWR }}
      </table-bar-value>
    </td>
    <td
      v-if="$can('view-event-feedback')"
      style="width: 1%"
    >
      <div
        v-if="data.event_stats.length === 0"
        class="grey--text caption"
      >
        N/A
      </div>
      <div
        v-else
      >
        <training-load-chip
          v-for="eventStat in data.event_stats"
          :key="'event_stat_'+data.id+eventStat.id"
          :training-load="eventStat.player_stats.TL"
          :training-grade="eventStat.player_stats.TG"
          :session-enjoyment="eventStat.player_stats.ENJY"
          :event-start-at="eventStat.start_at"
          :event-end-at="eventStat.end_at"
          :event-title="eventStat.title"
          :training-duration="eventStat.player_stats.TTM"
          :player-timezone="player.settings && player.settings.timezone"
        />
      </div>
    </td>
    <td>
      <table-bar-value
        :value="data.stats.DL"
        :max="maxValues.DL"
        color="grey lighten-2"
      >
        {{ data.stats.DL }}
      </table-bar-value>
    </td>
    <td>
      <table-bar-value
        :value="data.stats.AL"
        :max="maxValues.AL"
        color="grey lighten-2"
      >
        {{ data.stats.AL }}
      </table-bar-value>
    </td>
    <td>
      <table-bar-value
        :value="data.stats.CL"
        :max="maxValues.CL"
        color="grey lighten-2"
      >
        {{ data.stats.CL }}
      </table-bar-value>
    </td>
    <td>
      <table-bar-value
        :value="data.stats.WL"
        :max="maxValues.WL"
        color="grey lighten-2"
      >
        {{ data.stats.WL }}
      </table-bar-value>
    </td>
    <td>
      {{ data.stats.WC >= 0 ? '+' : '' }}{{ data.stats.WC }}
      <v-icon
        v-if="data.stats.WC !== 0"
        small
        class="ml-1"
        :color="data.stats.WC > 0 ? 'green' : 'red'"
      >
        {{ data.stats.WC > 0 ? 'fal fa-arrow-trend-up' : 'fal fa-arrow-trend-down' }}
      </v-icon>
    </td>
    <td>
      <table-bar-value
        :value="data.stats.FRES"
        :max="maxValues.FRES"
        color="grey lighten-2"
      >
        {{ data.stats.FRES }}
      </table-bar-value>
    </td>
    <td>
      <table-bar-value
        :value="data.stats.MON"
        :max="maxValues.MON"
        color="grey lighten-2"
      >
        {{ data.stats.MON }}
      </table-bar-value>
    </td>
    <td v-if="showWellnessData">
      <table-bar-value
        :value="data.stats.STR"
        :max="maxValues.STR"
        color="grey lighten-2"
      >
        {{ data.stats.STR }}
      </table-bar-value>
    </td>
    <td
      v-if="showWellnessData"
      style="width: 0"
    >
      <table-selection-chip-value
        :value="data.stats.GMS"
        :max="5"
        reverse-colors
      />
    </td>
    <td v-if="showWellnessData">
      <table-selection-chip-value
        :value="data.stats.STL"
        :max="5"
        reverse-colors
      />
    </td>
    <td v-if="showWellnessData">
      <table-selection-chip-value
        :value="data.stats.SLQ"
        :max="5"
      />
    </td>
    <td v-if="showWellnessData">
      <table-selection-chip-value
        :value="data.stats.MOO"
        :max="5"
      />
    </td>
    <td>
      <pain-label
        :label="data.stats.highest_pain ? data.stats.highest_pain.label : undefined"
        :value="data.stats.highest_pain ? data.stats.highest_pain.value : undefined"
        :player-stats="data.stats"
        :gender="player.gender"
      />
    </td>
    <td>
      <sickness-label :player-stats="data.stats" />
    </td>
    <td v-if="showMenstrualPeriod">
      <div
        v-if="data.stats.FMENSACT !== undefined"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <v-chip
              label
              outlined
              small
              :color="data.stats.FMENSACT ? 'blue' : 'grey'"
              v-on="on"
            >
              {{ data.stats.FMENSACT ? $t('status.yes') : $t('status.no') }}
              <span
                v-if="data.stats.FMENSFR"
                class="ml-1"
              >
                ({{ data.stats.FMENSFR }})
              </span>
            </v-chip>
          </template>
          <div>
            <div v-if="data.stats.FMENSFR">
              {{ $t('individual_analysis.menstrual_period.variable.flow_rate') }} <strong>{{ data.stats.FMENSFR }}</strong>
            </div>
          </div>
        </v-tooltip>
      </div>
      <div
        v-else
        class="grey--text caption"
      >
        N/A
      </div>
    </td>
    <td v-if="showMenstrualPeriod">
      <menstrual-phases
        v-if="data.stats.cycle_phase && data.stats.cycle_phase !== noPhase"
        :current-menstrual-phase="data.stats.cycle_phase"
        :stats="data.stats"
      />
      <span
        v-else
        class="grey--text caption"
      >
        N/A
      </span>
    </td>
  </tr>
</template>

<script>
import TableBarValue from '@/components/helpers/TableBarValue'
import TableSelectionChipValue from '@/components/helpers/TableSelectionChipValue'
import TrainingLoadChip from '@/components/helpers/TrainingLoadChip'
import { getColorForValueWithDefinition } from '@/utils/helpers'
import { acwrColorsDefinition } from '@/data/models/Stats'
import PainLabel from '@/components/helpers/PainLabel'
import MenstrualPhases from '@/components/helpers/MenstrualPhases.vue'
import { MenstrualPhase } from '@/data/models'
import SicknessLabel from '@/components/helpers/SicknessLabel.vue'

export default {
  name: 'PlayerTrainingLoadTableRow',
  components: { SicknessLabel, MenstrualPhases, PainLabel, TrainingLoadChip, TableSelectionChipValue, TableBarValue },
  props: {
    data: {
      type: Object,
      required: true
    },
    maxValues: {
      type: Object,
      default: () => ({})
    },
    showMenstrualPeriod: {
      type: Boolean,
      default: false
    },
    showWellnessData: {
      type: Boolean,
      default: false
    },
    player: {
      type: Object,
      default: () => ({})
    }
  },
  created() {
    this.noPhase = MenstrualPhase.NO_PHASE
  },
  methods: {
    getAcwrColorFromValue(value) {
      return getColorForValueWithDefinition(value, acwrColorsDefinition)
    }
  }
}
</script>
